// Reducer for updating the Swap screen from Swap actions

import { Token } from '../../../logic/token/token'
import { Wei } from '../../../utils/types'
import { oracleApi } from '../../../api/oracle/oracle-client'
import {
  fromWei,
  numericTextAsWei,
  weiToNumericText,
} from '../../../utils/static-web3'
import { Amounts } from '../../../logic/token/amounts'
import {
  createFailureEvent,
  createSuccessEvent,
  createTradeTabEvent,
} from 'user-events-segment'
import { Swap } from '../../../logic/swaps/Swap'

export interface SwapReducerState {
  oracleReady: boolean
  tokenIn: Token | undefined
  tokenOut: Token | undefined
  tokenInBalance: Wei
  exceed: boolean
  tokenInValue: number | undefined
  tokenOutValue: number | undefined
  tokenInText: string
  tokenInWei: Wei | undefined
  estimating: boolean
  expectedOutWei: Wei | undefined
  max: boolean
}

export interface SwapAction {
  type:
    | 'oracle-loaded'
    | 'selected-in'
    | 'selected-out'
    | 'update-tokenInBalance'
    | 'value-typed'
    | 'max'
    | 'reset'
    | 'estimating' // starts estimation
    | 'expectation' // completes estimation
    | 'expectation-cleared' // failed estimation
    | 'switch'
    | 'success'
    | 'failed'
    | 'started'
    | 'cancelled'
  data?: any
  selection?: Token
  valueText?: string
  maxValue?: Wei
  expectation?: Wei
  /** Swap object is different from SwapReducerState because
   * it still lives after the screen is reset, while the
   * transaction is being processed.
   * Aka, there is one SwapReducerState, 0 or many Swap objects
   */
  swap?: Swap
  selectionMode?: 'selected_default' | undefined
  tokenInBalance?: Wei | undefined
}

export const SWAP_INITIAL_STATE: SwapReducerState = {
  oracleReady: false,
  tokenIn: undefined,
  tokenOut: undefined,
  tokenInBalance: '0',
  exceed: false,
  tokenInValue: undefined,
  tokenOutValue: undefined,
  tokenInText: '',
  estimating: false,
  expectedOutWei: undefined,
  tokenInWei: undefined,
  max: false,
}

export function swapReducer(
  state: SwapReducerState,
  action: SwapAction
): SwapReducerState {
  let nextState: SwapReducerState
  switch (action.type) {
    case 'oracle-loaded':
      return {
        ...state,
        oracleReady: true,
      }
    case 'selected-in':
      const amount = action.selection
        ? numericTextAsWei(state.tokenInText, action.selection.decimals)
        : undefined
      nextState = {
        ...state,
        tokenIn: action.selection,
        max: false,
        tokenInWei: weiValue(action.selection!, state.tokenInText),
        tokenInValue: dollarValue(action.selection!, amount),
        tokenOutValue: undefined,
        expectedOutWei: undefined,
      }
      nextState.estimating = willStartANewEstimation(nextState, action)
      if (action.selectionMode !== 'selected_default') {
        createTradeTabEvent().selected_token(
          'in',
          state.tokenIn ? state.tokenIn.symbol : '',
          nextState.tokenIn ? nextState.tokenIn.symbol : ''
        )
      }
      updateExceedState(nextState)

      return nextState
    case 'update-tokenInBalance':
      nextState = {
        ...state,
        tokenInBalance: action.tokenInBalance!,
      }
      updateExceedState(nextState)
      return nextState
    case 'selected-out':
      nextState = {
        ...state,
        tokenOut: action.selection,
        tokenOutValue: undefined,
        expectedOutWei: undefined,
      }
      nextState.estimating = willStartANewEstimation(nextState, action)
      createTradeTabEvent().selected_token(
        'out',
        state.tokenOut ? state.tokenOut.symbol : '',
        nextState.tokenOut ? nextState.tokenOut.symbol : ''
      )
      return nextState
    case 'value-typed':
      const text = action.valueText

      let valueWei = weiValue(state.tokenIn!, text)
      nextState = {
        ...state,
        tokenInText: text || '',
        tokenInWei: valueWei,
        max: false,
        tokenInValue: dollarValue(state.tokenIn, valueWei),
        // we are estimating, or things are wrong, anyway, no more out value
        tokenOutValue: undefined,
        expectedOutWei: undefined,
      }
      nextState.estimating = willStartANewEstimation(nextState, action)
      if (nextState.tokenIn) {
        createTradeTabEvent().entered_amount_token(
          nextState.tokenIn.symbol,
          fromWei(valueWei!, state.tokenIn!.decimals)
        )
      }
      updateExceedState(nextState)

      return nextState
    case 'max':
      const maxValueWei = weiValue(state.tokenIn, action.valueText)
      const maxValueDollar = dollarValue(state.tokenIn, maxValueWei)

      nextState = {
        ...state,
        max: true,
        tokenInText: action.valueText || '',
        tokenInValue: maxValueDollar,
        tokenInWei: action.maxValue,
      }
      nextState.estimating = willStartANewEstimation(nextState, action)
      createTradeTabEvent().clicked_max_token(
        state.tokenIn!.symbol,
        fromWei(action.maxValue || '0', state.tokenIn!.decimals)
      )
      updateExceedState(nextState)

      return nextState

    case 'reset':
      return {
        ...SWAP_INITIAL_STATE,
        tokenIn: state.tokenIn,
        tokenOut: state.tokenOut,
      }
    case 'expectation':
      const tokenOutValue =
        oracleApi.getPrice(state.tokenOut!.symbol) *
        fromWei(action.expectation!, state.tokenOut!.decimals)
      return {
        ...state,
        estimating: false,
        expectedOutWei: action.expectation,
        tokenOutValue,
      }
    case 'expectation-cleared':
      return {
        ...state,
        estimating: false,
        expectedOutWei: undefined,
      }
    case 'switch':
      const switchedState = switchState(state, action)
      const inSymbol = state.tokenIn ? state.tokenIn.symbol : ''
      const outSymbol = state.tokenOut ? state.tokenOut.symbol : ''

      createTradeTabEvent().switched_trade_tokens([inSymbol, outSymbol])
      updateExceedState(switchedState)

      return switchedState
    case 'success':
      const swap = action.swap!
      fireSuccessEvent(swap)
      return state
    case 'failed':
      const failedSwap = action.swap!
      fireFailedEvent(action.data.step, action.data.reason, failedSwap)
      return state
  }
  return state
}

function switchState(
  state: SwapReducerState,
  action: SwapAction
): SwapReducerState {
  let futureOutWei = undefined
  let futureTextIn = undefined

  // Case where are able
  if (isValidAmount(state.expectedOutWei)) {
    futureOutWei = numericTextAsWei(state.tokenInText, state.tokenIn!.decimals)
  }

  if (state.expectedOutWei && state.tokenOut) {
    futureTextIn = weiToNumericText(
      state.expectedOutWei,
      state.tokenOut.decimals
    ).toString()
  }

  const nextState = {
    ...state,
    tokenIn: state.tokenOut,
    tokenOut: state.tokenIn,
    tokenInWei: state.expectedOutWei,
    tokenInText: futureTextIn || '',
    tokenInValue: dollarValue(state.tokenOut, state.expectedOutWei),
    tokenOutValue: dollarValue(state.tokenIn, futureOutWei),
    expectedOutWei: futureOutWei,
  }
  nextState.estimating = willStartANewEstimation(nextState, action)
  return nextState
}

function updateExceedState(nextState: SwapReducerState): SwapReducerState {
  const inWei = Number(nextState.tokenInWei)
  const inBal = isValidAmount(nextState.tokenInBalance)
    ? Number(nextState.tokenInBalance)
    : 0

  if (!isValidAmount(nextState.tokenInWei)) {
    nextState.exceed = false
  } else {
    nextState.exceed = inWei > inBal
  }

  return nextState
}

function willStartANewEstimation(
  nextState: SwapReducerState,
  action: any
): boolean {
  return !!(
    nextState.tokenIn &&
    nextState.tokenOut &&
    isValidAmount(nextState.tokenInText)
  )
}

export function isValidAmount(val: string | undefined) {
  if (!val) {
    return false
  }
  const float = parseFloat(val)
  return !isNaN(float) && float > 0
}

function dollarValue(
  token: Token | undefined,
  amountWei: Wei | undefined
): number | undefined {
  if (token === undefined) {
    return undefined
  }
  const valid = amountWei && isValidAmount(amountWei)
  if (!valid) {
    return undefined
  }
  const amount = fromWei(amountWei, token.decimals)
  return oracleApi.getPrice(token.symbol) * Number(amount)
}

function weiValue(
  token: Token | undefined,
  text: string | undefined
): Wei | undefined {
  if (token === undefined) {
    return undefined
  }
  const valid = text && isValidAmount(text)

  return valid ? numericTextAsWei(text!, token.decimals)! : undefined
}

function fireSuccessEvent(swap: Swap) {
  oracleApi.getPrices().then(() => {
    const amountIn = fromWei(swap.inAmountWei!, swap.tokenIn.decimals)
    const out = swap.tokenOut
    const expected: number = fromWei(swap.expectedOutAmountWei!, out.decimals)
    const minimum = fromWei(swap.calculateMinAmountWei(), out.decimals)
    const real = fromWei(swap.realOutAmountWei!, out.decimals)
    createSuccessEvent().trade_success(
      swap.transactionHash!,
      swap.tokenIn.symbol,
      out.symbol,
      amountIn,
      expected,
      real,
      minimum,
      swap.slippage,
      oracleApi.getAmountPrice(swap.tokenIn, swap.inAmountWei!)
    )
  })
}

function fireFailedEvent(step: string, reason: string, swap: Swap) {
  oracleApi.getPrices().then(() => {
    const amountIn = fromWei(swap.inAmountWei!, swap.tokenIn.decimals)
    const out = swap.tokenOut
    const expected: number = swap.expectedOutAmountWei
      ? fromWei(swap.expectedOutAmountWei, out.decimals)
      : -1
    const minimum = fromWei(swap.calculateMinAmountWei(), out.decimals)
    createFailureEvent().trade_failure(
      step,
      reason,
      swap.tokenIn.symbol,
      out.symbol,
      amountIn,
      expected,
      minimum,
      swap.slippage,
      oracleApi.getAmountPrice(swap.tokenIn, swap.inAmountWei!),
      swap.transactionHash
    )
  })
}

function getAmounts(state: SwapReducerState): Amounts {
  const amounts = new Amounts()
  if (state.tokenIn && state.tokenInWei) {
    amounts.set(state.tokenIn, state.tokenInWei)
  }
  if (state.tokenOut && state.expectedOutWei) {
    amounts.set(state.tokenOut, state.expectedOutWei)
  }
  return amounts
}
